/** Browser-side exports: MP3 (lamejs) + WAV, and a download helper. No placeholder files — real encoded audio. */
import { encodeWav } from "@/lib/wavEncoder";

const toInt16 = (f32, start, end) => {
  const out = new Int16Array(Math.max(0, Math.min(end, f32.length) - start));
  for (let i = 0; i < out.length; i += 1) {
    const s = Math.max(-1, Math.min(1, f32[start + i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
};

/** Encode an AudioBuffer to an MP3 Blob (stereo when the source is). Yields to the UI while it works. */
export async function encodeMp3(buffer, kbps = 192) {
  const { Mp3Encoder } = await import("@breezystack/lamejs");
  const stereo = buffer.numberOfChannels > 1;
  const enc = new Mp3Encoder(stereo ? 2 : 1, buffer.sampleRate, kbps);
  const L = buffer.getChannelData(0);
  const R = stereo ? buffer.getChannelData(1) : null;
  const block = 1152 * 32;
  const parts = [];
  for (let i = 0, n = 0; i < buffer.length; i += block, n += 1) {
    const l = toInt16(L, i, i + block);
    const out = stereo ? enc.encodeBuffer(l, toInt16(R, i, i + block)) : enc.encodeBuffer(l);
    if (out.length) parts.push(new Uint8Array(out));
    if (n % 20 === 0) await new Promise((r) => setTimeout(r, 0));
  }
  const tail = enc.flush();
  if (tail.length) parts.push(new Uint8Array(tail));
  return new Blob(parts, { type: "audio/mpeg" });
}

export const encodeWavBlob = (buffer) => encodeWav(buffer, { bitDepth: 24 });

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export const safeName = (s) => (s || "ELPM").replace(/[^\w\s.-]+/g, "").trim().slice(0, 80) || "ELPM";

/** Encode + download as "<name>.wav" or "<name>.mp3". */
export async function exportBuffer(buffer, name, fmt) {
  const blob = fmt === "mp3" ? await encodeMp3(buffer) : encodeWavBlob(buffer);
  downloadBlob(blob, `${safeName(name)}.${fmt}`);
  return blob;
}
